import Modal from '../components/Modal/Modal'
import Header from '../components/Header/Header'
import Loading from '../components/Loading/Loading'
import HistoryTable from '../components/HistoryTable/HistoryTable'
import PopupMessage from '../components/PopupMessage/PopupMessage'
import HistoryItem from '../components/mobile-components/HistoryItem/HistoryItem'
import BoxSelectButton from '../components/TableButtons/BoxSelectButton'
import { Link } from 'react-router-dom'

import { useEffect, useContext, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getProfile } from '../api/users'

import { AuthContext } from '../contexts/AuthContext'
import {
    formatAmount,
    formatDate,
    debtsPaymentDescription,
} from '../helpers/formatters'
import { getCurrentTimestamp } from '../helpers/time'

import useMediaQuery from '../hooks/useMediaQuery'
import useAuth from '../hooks/useAuth'
import { fetchAPI } from '../helpers/api'
import { getConfirmedDebts } from '../api/debts'

const DebtsConfirmed = () => {
    useEffect(() => {
        document.title =
            'Βεβαιωμένες Οφειλές - Πλατφόρμα Ηλ. Πληρωμών Δήμου Αντιπάρου'
    }, [])

    useEffect(() => {
        const script = document.createElement('script')

        script.src =
            'https://ibanke-commerce.nbg.gr/static/checkout/checkout.min.js'

        document.head.appendChild(script)

        return () => document.head.removeChild(script)
    }, [])

    const isMobile = useMediaQuery('(max-width:1024px)')
    const [popup, setPopup] = useState({ message: '', type: '', shown: false })
    const [loadingForPay, setLoadingForPay] = useState(false)
    const { token } = useContext(AuthContext)
    const { refreshToken, getCurrentUserID } = useAuth()
    const options = {
        headers: {
            Authorization: `Bearer ${token.access_token}`,
        },
    }

    const userID = getCurrentUserID()

    const { isLoading, data } = useQuery({
        queryKey: ['confirmedDebts', options],
        queryFn: () => getConfirmedDebts(options),
    })

    const [debtsToPay, setDebtsToPay] = useState({
        totalAmount: 0,
        debtsId: [],
    })

    const debtsPaymentDescriptionString = debtsPaymentDescription(
        'Πληρωμή οφειλών',
        debtsToPay.debtsId
    )

    if (isLoading || loadingForPay) {
        return <Loading />
    }

    const initiateCheckout = async () => {
        const profileData = await getProfile(options)
        const profile = profileData?.profile
        let fullName
        profile
            ? (fullName = `${profile?.firstName} ${profile?.lastName}`)
            : (fullName = '-')
        try {
            // Refresh the tokens before initiating the NBG checkout
            refreshToken()
            const checkoutBody = {
                payment: {
                    description: debtsPaymentDescriptionString,
                    paymentCode: getCurrentTimestamp().toString(),
                    amount: debtsToPay.totalAmount,
                    associateTitle: fullName,
                    debts: debtsToPay.debtsId,
                },
                userId: userID,
            }

            const res = await fetchAPI(
                `${process.env.REACT_APP_API_URL}/payments/initiateCheckout`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token.access_token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(checkoutBody),
                }
            )

            // eslint-disable-next-line no-undef
            Checkout.configure({
                session: {
                    id: res.data.session.id,
                },
            })

            // eslint-disable-next-line no-undef
            Checkout.showPaymentPage()
        } catch (err) {
            console.error(err)
            return false
        }
    }

    const submitHandler = async e => {
        e.preventDefault()

        if (debtsToPay.debtsId.length === 0) {
            setPopup(values => ({
                ...values,
                shown: true,
                message: (
                    <>
                        <p>
                            Παρακαλώ επιλέξτε τουλάχιστον μία οφειλή προς
                            πληρωμή.
                        </p>
                    </>
                ),
                type: 'error',
            }))

            return
        }

        setPopup(values => ({
            ...values,
            shown: true,
            message: (
                <>
                    <p>Η διαδικασία πληρωμής ακυρώθηκε.</p>
                    <p>Παρακαλώ προσπαθήστε ξανά.</p>
                </>
            ),
            type: 'error',
        }))

        setLoadingForPay(true)

        setTimeout(() => {
            setPopup(values => ({
                ...values,
                shown: true,
                message: (
                    <>
                        <p>Η διαδικασία πληρωμής ακυρώθηκε.</p>
                        <p>Παρακαλώ προσπαθήστε ξανά.</p>
                    </>
                ),
                type: 'errorPayment',
            }))

            setLoadingForPay(false)
        }, 3000)

        return
        // eslint-disable-next-line no-unreachable
        const nbgData = await initiateCheckout()

        if (!nbgData.success) {
            setPopup(values => ({
                ...values,
                shown: true,
                message: (
                    <>
                        {nbgData.message ? (
                            <p> {nbgData.message} </p>
                        ) : (
                            <>
                                <p>Κάτι πήγε στραβά! </p>
                                <p>Παρακαλώ προσπαθήστε ξανά.</p>
                            </>
                        )}
                    </>
                ),
                type: 'error',
            }))

            return
        }

        // eslint-disable-next-line no-undef
        Checkout.configure({
            session: {
                id: nbgData.data.session.id,
            },
        })

        // eslint-disable-next-line no-undef
        Checkout.showPaymentPage()
    }

    const headings = [
        '',
        'Κωδικός Οφειλής',
        'Χρηματικός Κατάλογος',
        'Παρατηρήσεις',
        'Ημερομηνία Βεβαίωσης',
        'Ημερομηνία Λήξης Πληρωμής',
        'Βεβαιωθέν Ποσό',
        'Προσαυξήσεις',
        'Σύνολο Οφειλής',
    ]

    const rows = data.debts.map(debt => {
        return {
            0: (
                <BoxSelectButton
                    key={debt.id}
                    debtId={debt.id}
                    debtTotalAmount={debt.totalAmount}
                    debtsToPay={debtsToPay}
                    setDebtsToPay={setDebtsToPay}
                />
            ),
            1: debt.id ?? '-',
            2: debt.moneylistReason ?? '-',
            3: debt.comments ?? '-',
            4: formatDate(debt.confirmationDate) ?? '-',
            5: formatDate(debt.dueDate) ?? '-',
            6: formatAmount(debt.confirmedAmount) ?? '-',
            7: formatAmount(debt.addedAmount) ?? '-',
            8: formatAmount(debt.totalAmount) ?? '-',
        }
    })

    const mobileTableHeadings = [
        'Κωδικός Οφειλής',
        'Χρηματικός Κατάλογος',
        'Παρατηρήσεις',
        'Ημερομηνία Βεβαίωσης',
        'Ημερομηνία Λήξης Πληρωμής',
        'Βεβαιωθέν Ποσό',
        'Προσαυξήσεις',
        'Σύνολο Οφειλής',
    ]

    return (
        <div className="flex flex-col">
            {popup.shown && (
                <PopupMessage
                    message={popup.message}
                    type={popup.type}
                    closePopUp={() =>
                        setPopup(values => ({ ...values, shown: false }))
                    }
                    showCloseButton={true}
                />
            )}

            {isMobile ? (
                <>
                    <h2 className="text-primary text-2xl text-center my-8">
                        Βεβαιωμένες Οφειλές
                    </h2>

                    {data.debts.length > 0 ? (
                        data.debts.map((debt, index) => {
                            return (
                                <HistoryItem
                                    key={debt.id}
                                    headings={mobileTableHeadings}
                                    rows={rows[index]}
                                />
                            )
                        })
                    ) : (
                        <h2 className="text-base text-primary font-bold text-center">
                            Δεν βρέθηκαν βεβαιωμένες οφειλές
                        </h2>
                    )}
                </>
            ) : (
                <Modal classes="w-full  max-w-7xl flex flex-col">
                    <Header
                        includeLogo={false}
                        withBorder={true}
                        includeButtons={true}
                    />
                    <h2 className="text-primary text-3xl font-light mt-12 mb-6 self-center">
                        Βεβαιωμένες Οφειλές
                    </h2>
                    <div className="overflow-auto">
                        <div className="w-[80%] lg:w-full flex flex-col justify-start items-center grow">
                            <HistoryTable
                                headings={headings}
                                rows={rows}
                                noDataText={
                                    <>
                                        <div>
                                            Παρακαλούμε χρησιμοποιήστε τη
                                            διαδικασία&nbsp;
                                            <Link
                                                className="font-bold text-olive"
                                                to={'/requests'}
                                            >
                                                αίτησης υπολοίπου&nbsp;
                                            </Link>
                                            για να ενημερωθείτε για τις οφειλές
                                            σας.
                                        </div>
                                    </>
                                }
                            />
                        </div>
                    </div>
                </Modal>
            )}

            {data?.debts.length > 0 && (
                <button
                    className="self-end m-10 bg-primary text-white border border-primary hover:bg-white hover:text-primary transition-all rounded-lg w-fit py-3.5 px-10"
                    type="submit"
                    onClick={submitHandler}
                >
                    {`Πληρωμή 
                    ${
                        debtsToPay.totalAmount !== 0
                            ? debtsToPay.totalAmount + ' €'
                            : null
                    }`}
                </button>
            )}
        </div>
    )
}

export default DebtsConfirmed
